import { useState } from 'react';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { Check, ChevronsUpDown, X } from 'lucide-react';

export interface MultiTypeaheadOption {
  /** Stable value sent to the API. */
  value: string;
  /** Human-readable label rendered in the dropdown and the chips. */
  label: string;
}

export interface MultiTypeaheadProps {
  /** Currently-selected values. */
  value: string[];
  /** Setter. */
  onChange: (next: string[]) => void;
  /** Available options. */
  options: MultiTypeaheadOption[];
  /** Placeholder shown in the input when nothing is selected. */
  placeholder?: string;
}

/**
 * Multi-select typeahead built on Headless UI's Combobox in `multiple` mode.
 *
 * - Selected values render as chips inside the trigger; click the chip's X
 *   to remove it without opening the dropdown.
 * - Typing filters the dropdown by case-insensitive substring on the label.
 * - Selecting an option toggles it in/out of the value array.
 * - Behaves like the single-select Typeahead used in the commission entry
 *   form, but allows multiple selections.
 */
export const MultiTypeahead = ({ value, onChange, options, placeholder }: MultiTypeaheadProps) => {
  const [query, setQuery] = useState('');

  const labelByValue = new Map(options.map((o) => [o.value, o.label]));
  const selectedValues = value;
  const selectedSet = new Set(selectedValues);

  const filtered =
    query === ''
      ? options
      : options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));

  const removeValue = (v: string) => {
    onChange(selectedValues.filter((x) => x !== v));
  };

  return (
    <Combobox value={selectedValues} onChange={(next: string[]) => onChange(next)} multiple>
      <div className="relative">
        <div className="input flex flex-wrap items-center gap-1 min-h-[2.5rem] py-1">
          {selectedValues.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded bg-blue-100 text-blue-800 text-xs px-2 py-0.5"
            >
              <span className="truncate max-w-[140px]">{labelByValue.get(v) ?? v}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  removeValue(v);
                }}
                className="text-blue-600 hover:text-blue-900"
                aria-label={`Remove ${labelByValue.get(v) ?? v}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <ComboboxInput
            className="flex-1 min-w-[60px] border-none outline-none p-0 text-sm bg-transparent"
            displayValue={() => query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={selectedValues.length === 0 ? placeholder : ''}
          />
          <ComboboxButton className="text-gray-400 shrink-0">
            <ChevronsUpDown className="h-4 w-4" />
          </ComboboxButton>
        </div>
        <ComboboxOptions className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-gray-500">Nothing found</div>
          ) : (
            filtered.map((option) => {
              const isSelected = selectedSet.has(option.value);
              return (
                <ComboboxOption
                  key={option.value}
                  value={option.value}
                  className={({ focus }) =>
                    `relative cursor-default select-none py-2 pl-9 pr-3 ${
                      focus ? 'bg-blue-600 text-white' : 'text-gray-900'
                    }`
                  }
                >
                  {({ focus }) => (
                    <>
                      <span className={`block truncate ${isSelected ? 'font-semibold' : 'font-normal'}`}>
                        {option.label}
                      </span>
                      {isSelected && (
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-2 ${
                            focus ? 'text-white' : 'text-blue-600'
                          }`}
                        >
                          <Check className="h-4 w-4" />
                        </span>
                      )}
                    </>
                  )}
                </ComboboxOption>
              );
            })
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
};

export default MultiTypeahead;
